import { useState } from "react";
import { api, errorText, useFetch } from "../api";
import { Alerts, Badge, EmptyRow, Field, Loading, Modal, Page } from "../components/ui";
import { label, money, text, when } from "../format";

const EMPTY = {
  name: "",
  shortName: "",
  contractAddress: "",
  decimals: "18",
  minDeposit: "",
  maxDeposit: "",
  depositStatus: "ACTIVE",
};

function contractText(row) {
  if (!row || !row.contractAddress) return "";
  if (typeof row.contractAddress === "string") return row.contractAddress;
  return row.contractAddress.BEP20 || "";
}

function decimalsText(row) {
  if (!row || row.decimals == null || row.decimals === "") return "18";
  if (typeof row.decimals === "object") return row.decimals.BEP20 ?? "";
  return row.decimals;
}

function formFrom(row) {
  if (!row) return EMPTY;
  return {
    name: row.name || "",
    shortName: row.shortName || "",
    contractAddress: contractText(row),
    decimals: decimalsText(row),
    minDeposit: row.minDeposit ?? "",
    maxDeposit: row.maxDeposit ?? "",
    depositStatus: row.depositStatus || "ACTIVE",
  };
}

function chainLabel(chain) {
  const list = Array.isArray(chain) ? chain : chain ? [chain] : [];
  if (!list.length) return "BEP20";
  return list.map((item) => label(item)).join(", ");
}

function currencyBody(form, initial) {
  const name = form.name.trim();
  const shortName = form.shortName.trim().toUpperCase();
  const contract = form.contractAddress.trim();
  const decimals = form.decimals === "" ? "" : Number(form.decimals);
  const minDeposit = form.minDeposit === "" ? "" : Number(form.minDeposit);
  const maxDeposit = form.maxDeposit === "" ? "" : Number(form.maxDeposit);

  if (!initial) {
    const body = {
      name,
      shortName,
      minDeposit,
      maxDeposit,
      depositStatus: form.depositStatus || "ACTIVE",
    };
    if (contract) body.contractAddress = contract;
    if (decimals !== "") body.decimals = decimals;
    return body;
  }

  const body = {};
  if (name !== (initial.name || "")) body.name = name;
  if (shortName !== (initial.shortName || "")) body.shortName = shortName;
  if (contract !== contractText(initial)) body.contractAddress = contract;
  if (Number(decimals) !== Number(decimalsText(initial))) body.decimals = decimals;
  if (Number(minDeposit) !== Number(initial.minDeposit)) body.minDeposit = minDeposit;
  if (Number(maxDeposit) !== Number(initial.maxDeposit)) body.maxDeposit = maxDeposit;
  if (form.depositStatus !== (initial.depositStatus || "ACTIVE")) body.depositStatus = form.depositStatus;
  return body;
}

export default function Currencies() {
  const list = useFetch("/currencies");
  const rows = Array.isArray(list.data?.data) ? list.data.data : [];
  const [editor, setEditor] = useState(null);
  const [notice, setNotice] = useState("");

  return (
    // actions={<button type="button" className="btn" onClick={() => setEditor(EMPTY)}>New coin</button>}
    <Page
      title="BEP20 coins"
      text="One row per coin the chain checker accepts. USDT on BSC is created automatically, with 18 decimals. The short name must match Settings currency if package purchases should spend that coin. Inactive stops new checks for that coin. The verify deposit path uses BSCSCAN_API. The 15-minute job uses BSCSCAN_API_VERIFY_ALL. Without the key for that path, that path finds nothing. Native BNB has no contract. The hourly sweep also needs BNBRPC and RECEIVE_ADD, the admin hot wallet address. The admin account must already have a BEP20 address row with the address and private key. The sweep does nothing until that row and both variables exist."
    >
      <Alerts error={list.error} notice={notice} />
      {list.loading ? <Loading /> : (
        <div className="table-wrap">
          <table className="wide">
            <thead>
              <tr>
                <th>Name</th>
                <th>Short name</th>
                <th>Chain</th>
                <th>Contract</th>
                <th>Decimals</th>
                <th className="num">Min deposit</th>
                <th className="num">Max deposit</th>
                <th>Status</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {rows.length ? rows.map((row) => (
                <tr key={row.id} className="click" onClick={() => setEditor(row)}>
                  <td>{row.name}</td>
                  <td className="code">{row.shortName}</td>
                  <td>{chainLabel(row.chain)}</td>
                  <td className="mono" title={contractText(row)}>{contractText(row) || "—"}</td>
                  <td>{text(decimalsText(row))}</td>
                  <td className="num">{money(row.minDeposit)}</td>
                  <td className="num">{money(row.maxDeposit)}</td>
                  <td><Badge value={String(row.depositStatus || "").toLowerCase()} /></td>
                  <td>{when(row.updatedAt)}</td>
                </tr>
              )) : <EmptyRow cols={9} text={list.error ? "Could not load coins" : "No coins"} />}
            </tbody>
          </table>
        </div>
      )}
      {editor ? (
        <CurrencyForm
          key={editor.id || "new"}
          initial={editor.id ? editor : null}
          onClose={() => setEditor(null)}
          onSaved={(message) => {
            setEditor(null);
            setNotice(message);
            list.reload();
          }}
        />
      ) : null}
    </Page>
  );
}

function CurrencyForm({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(formFrom(initial));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [errors, setErrors] = useState({});

  function set(key, value) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function onSubmit(event) {
    event.preventDefault();
    const fields = currencyBody(form, initial);
    if (initial && !Object.keys(fields).length) {
      setError("No changes to save");
      return;
    }
    setBusy(true);
    setError("");
    setErrors({});
    try {
      if (initial) await api("/currencies/" + initial.id, { method: "PATCH", body: fields });
      else await api("/currencies", { method: "POST", body: fields });
      onSaved(initial ? "Coin saved" : "Coin created");
    } catch (err) {
      setError(errorText(err));
      setErrors(err.errors || {});
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={initial ? "Edit coin" : "New coin"} wide onClose={onClose}>
      <form onSubmit={onSubmit}>
        <Alerts error={error} />
        <div className="form-grid">
          <Field label="Name" error={errors.name} hint="2 to 40 characters">
            <input maxLength={40} value={form.name} onChange={(event) => set("name", event.target.value)} />
          </Field>
          <Field label="Short name" error={errors.shortName} hint="Must match Settings currency if purchases should spend this coin">
            <input maxLength={10} value={form.shortName} onChange={(event) => set("shortName", event.target.value.toUpperCase())} />
          </Field>
          <div className="wide">
            <Field label="BEP20 contract" error={errors.contractAddress} hint="Leave empty for native BNB. USDT on BSC is 0x55d398326f99059fF775485246999027B3197955.">
              <input className="mono" value={form.contractAddress} onChange={(event) => set("contractAddress", event.target.value)} />
            </Field>
          </div>
          <Field label="Decimals" error={errors.decimals} hint="BEP20 decimal count, 0 to 36. Blank uses 18.">
            <input type="number" min="0" max="36" step="1" value={form.decimals} onChange={(event) => set("decimals", event.target.value)} />
          </Field>
          <Field label="Deposit status" error={errors.depositStatus} hint="Inactive stops new checks for this coin">
            <select value={form.depositStatus} onChange={(event) => set("depositStatus", event.target.value)}>
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
            </select>
          </Field>
          <Field label="Minimum deposit" error={errors.minDeposit} hint="Smallest amount that credits the wallet">
            <input type="number" min="0" step="0.01" value={form.minDeposit} onChange={(event) => set("minDeposit", event.target.value)} />
          </Field>
          <Field label="Maximum deposit" error={errors.maxDeposit} hint="Must be at least the minimum">
            <input type="number" min="0" step="0.01" value={form.maxDeposit} onChange={(event) => set("maxDeposit", event.target.value)} />
          </Field>
        </div>
        <p className="hint">The server always saves the chain as BEP20. The website sends this coin id as currencyId.</p>
        <div className="form-actions">
          <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn" disabled={busy}>{busy ? "Saving..." : "Save coin"}</button>
        </div>
      </form>
    </Modal>
  );
}
